import { ApexOptions } from 'apexcharts';
import React, { useContext, useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import { Box, Typography } from '@mui/material';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import { SendContext } from '../../SendContext';
import { EvoachButton } from '../Button';

// note, that tickAmount has not to be set when rendering the component as
// this is done in render...Action below! It happens within the options that
// are passed as an argument to the component.
// the tickAmound is set here anyway as we need it in widgetData
export interface PolarchartInputProps {
  options?: ApexOptions;
  series: number[];
  labels: string[];
  width?: number;
  title?: string;
  saveResultTo: string;
  buttonText: string;
  tickAmount?: number;
}

// no tickAmount here, see komment in props definition above
export const PolarchartInput: React.FC<PolarchartInputProps> = ({
  options,
  series,
  labels,
  title,
  saveResultTo,
  buttonText,
}) => {
  const { send } = useContext(SendContext);
  const [seriesEntries, setSeriesEntries] = useState<number[]>(series ?? []);
  const [isNextButtonEnabled, setNextButtonEnabled] = useState(false);

  // not necessary for component itself but for proper rendering in builder
  useEffect(() => {
    setSeriesEntries(series ?? []);
  }, [series]);

  const standardWidth = 500;

  return (
    <div>
      {title !== undefined && title.trim() !== '' && (
        <Typography
          variant="h5"
          style={{
            textAlign: 'center',
            fontWeight: 'bold',
            width: standardWidth,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ marginBottom: '10px', marginTop: '10px' }}>
        <Chart
          options={{
            ...options,
            labels: labels,
            series: seriesEntries,
          }}
          series={seriesEntries}
          type="polarArea"
          width={standardWidth}
          id="polarchartInput"
          key="polarchartInput"
          events={{
            updated: { setSeriesEntries },
            dataPointSelection: () => {
              setNextButtonEnabled(true);
            },
          }}
        />
      </Box>
      <EvoachButton
        disabled={isNextButtonEnabled}
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: { [saveResultTo]: seriesEntries },
          });
          send({ type: 'next' });
        }}
      >
        {buttonText}
      </EvoachButton>
    </div>
  );
};

export interface PolarchartInputComponent extends BaseComponent {
  type: 'polarchartInput';
  props: PolarchartInputProps;
}

export const defaultPolarchartInputOptions: ApexOptions = {
  stroke: {
    show: false,
  },
  chart: {
    animations: {
      enabled: false,
    },
    events: {
      // fires when user clicks on any area of the chart
      // The last parameter config contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts
      click: function (event, chartContext, config) {
        const seriesCount = config.config.yaxis[0].tickAmount;
        if (event.target.className.toString().includes('apexcharts-legend')) {
          return;
        }
        // normaliced clickpoints relative to center of polar chart
        let chartx =
          event.offsetX - (chartContext.axes.w.globals.gridWidth / 2 + 10);
        let charty =
          -1 * (event.offsetY - chartContext.axes.w.globals.gridHeight / 2);

        // size of circle
        let circleSize = chartContext.axes.w.globals.radialSize;

        // width of each segment along radius
        let diff = circleSize / (seriesCount - 1);
        // dist of clickpoint to middle of radar chart
        let disttocenter = Math.sqrt(chartx * chartx + charty * charty);
        // segment clicked
        let segment = Math.round(disttocenter / diff);
        if (disttocenter < diff) segment = 0;

        // do not accept clicks outside the circle
        if (disttocenter > circleSize) {
          return;
        }

        // 360 angle of currently clicked point
        let cosb = (Math.acos(charty / disttocenter) * 180) / Math.PI;
        if (chartx < 0) {
          cosb = 360 - cosb;
        }
        // number of pie chart elements
        let numseries = chartContext.series.w.globals.series.length;

        // angle per pie chart element
        let arcpercat = 360 / numseries;

        // clicked category
        let clickedcat = Math.ceil(cosb / arcpercat);

        config.config.series[clickedcat - 1] = segment + 1;
        chartContext.update();
      },
    },
  },
  colors: [
    '#FABD01',
    '#a867e6',
    '#F08144',
    '#4D70EA',
    '#FFE699',
    '#FFB2A0',
    '#4199AC',
    '#78934B',
  ],
  legend: {
    show: true,
    fontWeight: 400,
    fontSize: '14',
    position: 'bottom',
    itemMargin: {
      horizontal: 10,
      vertical: 5,
    },
  },
  fill: {
    opacity: 0.9,
    colors: [
      '#FABD01',
      '#a867e6',
      '#F08144',
      '#4D70EA',
      '#FFE699',
      '#FFB2A0',
      '#4199AC',
      '#78934B',
    ],
  },
  plotOptions: {
    area: {
      fillTo: 'end',
    },
    polarArea: {
      rings: {
        strokeWidth: 1,
        strokeColor: '#BEBEBE',
      },
      spokes: {
        strokeWidth: 1,
        connectorColors: '#BEBEBE',
      },
    },
  },
  yaxis: {
    show: false,
    max: 10,
    min: 1,
    tickAmount: 10,
  },
  grid: {
    position: 'front',
    borderColor: 'black',
    yaxis: {
      lines: {
        show: false,
      },
    },
    padding: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
  tooltip: {
    onDatasetHover: {
      highlightDataSeries: false,
    },
  },
  markers: {
    onClick: function () {
      return;
    },
  },
  states: {
    hover: {
      filter: {
        type: 'none',
      },
    },
    active: {
      filter: {
        type: 'none',
      },
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 450,
        },
        legend: {
          position: 'bottom',
        },
      },
    },
  ],
};

export const renderPolarchartInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    //console.log(payload);

    let polaroptions = payload.options;

    if (polaroptions === undefined || polaroptions === '') {
      polaroptions = defaultPolarchartInputOptions;
    }

    // max number of segments (default is 10)
    if (payload.tickAmount) {
      polaroptions.yaxis.tickAmount = +payload.tickAmount;
      polaroptions.yaxis.max = +payload.tickAmount;
    }

    // if series payload is set, take that, if not, empty series
    let series: number[] = payload.series ? payload.series : [];
    let labels: string[] = [];

    const getLabelsFrom = payload.getLabelsFrom;
    const getSeriesFrom = payload.getSeriesFrom;

    series = getSeriesFrom
      ? series.concat(context.userData[getSeriesFrom])
      : series.concat([]);

    // in v2 of PolarChart State Entry in Builder,
    // there might be two new variables available. At least,
    // getLabelsFrom should be available for input.
    if ((getLabelsFrom + '').trim() !== '') {
      // auto-generation of labels + series
      //console.log('autogen');
      if (
        context.userData[getLabelsFrom] &&
        (context.userData[getLabelsFrom] + '').trim() !== ''
      ) {
        labels = context.userData[getLabelsFrom].map((labelId: string) =>
          translateWithFallback(intl, labelId)
        );
      } else {
        labels = [];
      }
    } else {
      // if not autogenerated, then labels are explicitely defined
      //console.log('no autogen');
      labels = payload.labels.map((labelId: string) =>
        translateWithFallback(intl, labelId)
      );
    }

    // var names are in the array in context.userData[actionMetadata.action.payload.getValueFrom]
    // iterate this array and get the vars
    // if there are values, override and delete any potentially passed series input
    if (
      Array.isArray(payload.getValuesFrom) &&
      payload.getValuesFrom.length > 0 &&
      payload.getValuesFrom.every(
        (varname: string) =>
          varname.trim() !== '' && varname !== undefined && varname !== null
      )
    ) {
      //console.log('get values NEW: ' + payload.getValuesFrom);
      series = [];
      payload.getValuesFrom.forEach((varname: string) => {
        if (
          context.userData[varname] !== undefined &&
          context.userData[varname] !== null
        ) {
          series.push(context.userData[varname]);
        }
      });
      series = series.flat();
    }

    // if there are labels but no series values at all, fill them with half the tickAmount
    if (labels.length > 0 && series.length < labels.length) {
      const delta = labels.length - series.length;
      for (let i = 1; i <= delta; i++) {
        series.push(Math.round(polaroptions.yaxis.tickAmount / 2));
      }
    }

    //console.log(series);
    //console.log(labels);

    // push widget
    newContext.widgetData.push({
      type: 'polarchartInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        options: polaroptions as ApexOptions,
        series: series,
        labels: labels,
        width: payload.width > 650 ? 650 : payload.width,
        title: translateWithFallback(intl, payload.title),
        saveResultTo: payload.saveResultTo,
        tickAmount: payload.tickAmount ?? 10,
        buttonText: translateWithFallback(intl, payload.buttonText),
      },
    });

    return newContext;
  });
};
